import { useMemo, useEffect } from "react";
import { useQuery } from "react-query";
import { apollo } from "./apollo";
import { GET_INTEGRATION_LIST } from "./graphql";
import usePolicyStore from "../store/policy";

/**
 * Shared hook for integration selection.
 * Fetches the integration list, resolves the selected integration from the store,
 * and auto-selects when only one integration exists.
 *
 * @returns {{ integrationList: Array|null, integration: Object|null, setIntegration: Function, isLoading: boolean }}
 */
export default function useIntegration() {
  const { integrationUuid, integration, setIntegration, setIntegrationUuid } =
    usePolicyStore();

  const fetchIntegrationList = useQuery({
    queryKey: "integration_list",
    queryFn: async () => {
      const { data } = await apollo.query({
        query: GET_INTEGRATION_LIST,
        variables: { page: 1, first: 100 },
      });
      return data?.getIntegrationList;
    },
    keepPreviousData: true,
    staleTime: 60000,
  });

  const integrationList = useMemo(
    () =>
      fetchIntegrationList?.isSuccess ? fetchIntegrationList?.data?.data : null,
    [fetchIntegrationList],
  );

  // Resolve the full integration object from the UUID whenever list or UUID changes
  const resolvedIntegration = useMemo(() => {
    if (!integrationList || !integrationUuid) return null;
    return (
      integrationList.find((i) => i.integrationUuid === integrationUuid) || null
    );
  }, [integrationUuid, integrationList]);

  // Keep store's integration object in sync with the resolved one
  useEffect(() => {
    if (
      resolvedIntegration &&
      resolvedIntegration?.integrationUuid !== integration?.integrationUuid
    ) {
      setIntegration(resolvedIntegration);
    }
  }, [resolvedIntegration]);

  // Auto-select if only one integration exists and none is selected
  useEffect(() => {
    if (integrationList?.length === 1 && !integrationUuid) {
      setIntegration(integrationList[0]);
    }
  }, [integrationList, integrationUuid]);

  return {
    integrationList,
    integration: resolvedIntegration,
    integrationUuid,
    setIntegration,
    setIntegrationUuid,
    isLoading: fetchIntegrationList.isLoading,
  };
}
